'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import type { SpartanProductExteriorThreesixtyCanvasHandle, SpartanProductExteriorThreesixtyCanvasProps } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/canvas/types';
import { getCanvasPixelSize, shouldDesynchronizeThreesixtyCanvas, THREESIXTY_CANVAS_HEIGHT, THREESIXTY_CANVAS_WIDTH } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/canvas/utils';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
export const SpartanProductExteriorThreesixtyCanvas = t0 => {
  const $ = _c(37);
  const {
    ref,
    loader,
    alt,
    className,
    sourceKey,
    testId,
    renderAtSourceSize: t1,
    onReady,
    onDraw
  } = t0;
  const renderAtSourceSize = t1 === undefined ? false : t1;
  const {
    getFrameImage,
    getNearestFrameImage,
    getNearestFrameIndex,
    setDisplayedIndex,
    subscribeFrameReady
  } = loader;
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const currentIndexRef = useRef(0);
  const displayedIndexRef = useRef(null);
  const notifiedDisplayedIndexRef = useRef(null);
  const displayImageRef = useRef(null);
  const drawnImageRef = useRef(null);
  const hasSizedForSourceRef = useRef(false);
  const largestSizedSourceWidthRef = useRef(0);
  const frameReadyAnimationFrameRef = useRef(null);
  const isExactFrameRef = useRef(false);
  const hasReportedReadyRef = useRef(false);
  const onReadyRef = useRef(onReady);
  const onDrawRef = useRef(onDraw);
  let t2;
  let t3;
  if ($[0] !== onReady) {
    t2 = () => {
      onReadyRef.current = onReady;
    };
    t3 = [onReady];
    $[0] = onReady;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== onDraw) {
    t4 = () => {
      onDrawRef.current = onDraw;
    };
    t5 = [onDraw];
    $[3] = onDraw;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      displayImageRef.current = null;
      displayedIndexRef.current = null;
      notifiedDisplayedIndexRef.current = null;
      drawnImageRef.current = null;
      isExactFrameRef.current = false;
      hasReportedReadyRef.current = false;
      hasSizedForSourceRef.current = false;
      largestSizedSourceWidthRef.current = 0;
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== sourceKey) {
    t7 = [sourceKey];
    $[7] = sourceKey;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = image => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      const context = contextRef.current ?? canvas.getContext("2d", {
        desynchronized: shouldDesynchronizeThreesixtyCanvas()
      });
      if (!context) {
        return;
      }
      contextRef.current = context;
      if (!image || image.naturalWidth <= 0 || image.naturalHeight <= 0 || canvas.width <= 0 || canvas.height <= 0) {
        context.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }
      const scale = Math.min(canvas.width / image.naturalWidth, canvas.height / image.naturalHeight);
      const width = image.naturalWidth * scale;
      const height = image.naturalHeight * scale;
      if (width < canvas.width || height < canvas.height) {
        context.clearRect(0, 0, canvas.width, canvas.height);
      }
      if (canvas.width === image.naturalWidth && canvas.height === image.naturalHeight) {
        context.drawImage(image, 0, 0);
      } else {
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = "high";
        context.drawImage(image, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
      }
      drawnImageRef.current = image;
      onDrawRef.current?.(canvas);
      if (!hasReportedReadyRef.current && isExactFrameRef.current) {
        hasReportedReadyRef.current = true;
        onReadyRef.current?.();
      }
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const drawFrame = t8;
  let t9;
  if ($[10] !== renderAtSourceSize) {
    t9 = () => {
      const canvas_0 = canvasRef.current;
      if (!canvas_0) {
        return;
      }
      const image_0 = displayImageRef.current;
      const sourceSize = image_0 ? {
        width: image_0.naturalWidth,
        height: image_0.naturalHeight
      } : undefined;
      const {
        width: width_0,
        height: height_0
      } = renderAtSourceSize && sourceSize ? sourceSize : getCanvasPixelSize(canvas_0, sourceSize);
      if (canvas_0.width !== width_0 || canvas_0.height !== height_0) {
        canvas_0.width = width_0;
        canvas_0.height = height_0;
        drawnImageRef.current = null;
      }
      drawFrame(image_0);
    };
    $[10] = renderAtSourceSize;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const resize = t9;
  let t10;
  if ($[12] !== getFrameImage || $[13] !== getNearestFrameImage || $[14] !== getNearestFrameIndex || $[15] !== resize || $[16] !== setDisplayedIndex) {
    t10 = (index, t11) => {
      const {
        notifyLoader: t12
      } = t11 === undefined ? {} : t11;
      const notifyLoader = t12 === undefined ? true : t12;
      if (frameReadyAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(frameReadyAnimationFrameRef.current);
        frameReadyAnimationFrameRef.current = null;
      }
      currentIndexRef.current = index;
      const exactImage = getFrameImage(index);
      const nearestImage = exactImage ? null : getNearestFrameImage(index);
      const nearestIndex = nearestImage ? getNearestFrameIndex?.(index) : null;
      const image_1 = exactImage ?? nearestImage ?? displayImageRef.current;
      isExactFrameRef.current = exactImage !== null;
      const nextDisplayedIndex = exactImage ? index : nearestIndex;
      if (nextDisplayedIndex !== null && nextDisplayedIndex !== undefined) {
        displayedIndexRef.current = nextDisplayedIndex;
        if (notifyLoader && nextDisplayedIndex !== notifiedDisplayedIndexRef.current) {
          notifiedDisplayedIndexRef.current = nextDisplayedIndex;
          setDisplayedIndex?.(nextDisplayedIndex);
        }
      }
      displayImageRef.current = image_1;
      if (image_1 === drawnImageRef.current) {
        return;
      }
      if (image_1 && (!hasSizedForSourceRef.current || image_1.naturalWidth > largestSizedSourceWidthRef.current)) {
        hasSizedForSourceRef.current = true;
        largestSizedSourceWidthRef.current = image_1.naturalWidth;
        resize();
        return;
      }
      drawFrame(image_1);
    };
    $[12] = getFrameImage;
    $[13] = getNearestFrameImage;
    $[14] = getNearestFrameIndex;
    $[15] = resize;
    $[16] = setDisplayedIndex;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const drawIndex = t10;
  let t11;
  let t12;
  if ($[18] !== drawIndex || $[19] !== resize) {
    t11 = () => ({
      drawIndex,
      resize,
      getElement: () => canvasRef.current,
      getDisplayedIndex: () => displayedIndexRef.current
    });
    t12 = [drawIndex, resize];
    $[18] = drawIndex;
    $[19] = resize;
    $[20] = t11;
    $[21] = t12;
  } else {
    t11 = $[20];
    t12 = $[21];
  }
  useImperativeHandle(ref, t11, t12);
  let t13;
  let t14;
  if ($[22] !== drawIndex || $[23] !== subscribeFrameReady) {
    t13 = () => {
      drawIndex(currentIndexRef.current);
      const unsubscribe = subscribeFrameReady(() => {
        if (frameReadyAnimationFrameRef.current !== null) {
          return;
        }
        frameReadyAnimationFrameRef.current = window.requestAnimationFrame(() => {
          frameReadyAnimationFrameRef.current = null;
          drawIndex(currentIndexRef.current);
        });
      });
      return () => {
        unsubscribe();
        if (frameReadyAnimationFrameRef.current !== null) {
          window.cancelAnimationFrame(frameReadyAnimationFrameRef.current);
          frameReadyAnimationFrameRef.current = null;
        }
      };
    };
    t14 = [drawIndex, subscribeFrameReady];
    $[22] = drawIndex;
    $[23] = subscribeFrameReady;
    $[24] = t13;
    $[25] = t14;
  } else {
    t13 = $[24];
    t14 = $[25];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[26] !== resize) {
    t15 = () => {
      const canvas_1 = canvasRef.current;
      if (!canvas_1) {
        return;
      }
      let animationFrameId = null;
      const observer = new ResizeObserver(() => {
        if (animationFrameId !== null) {
          window.cancelAnimationFrame(animationFrameId);
        }
        animationFrameId = window.requestAnimationFrame(resize);
      });
      observer.observe(canvas_1);
      return () => {
        if (animationFrameId !== null) {
          window.cancelAnimationFrame(animationFrameId);
        }
        observer.disconnect();
      };
    };
    t16 = [resize];
    $[26] = resize;
    $[27] = t15;
    $[28] = t16;
  } else {
    t15 = $[27];
    t16 = $[28];
  }
  useEffect(t15, t16);
  let t17;
  if ($[29] !== className) {
    t17 = cn("block size-full", className);
    $[29] = className;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== testId) {
    t18 = createDataAttributes({
      test: testId
    });
    $[31] = testId;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== alt || $[34] !== t17 || $[35] !== t18) {
    t19 = <canvas ref={canvasRef} role="img" aria-label={alt} width={THREESIXTY_CANVAS_WIDTH} height={THREESIXTY_CANVAS_HEIGHT} className={t17} {...t18}>{alt}</canvas>;
    $[33] = alt;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  return t19;
};